import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import type { UseARAgingResult } from '@hooks/dashboard/useARAging'
import { formatChartCurrencyDollars } from '@utils/dashboardFinance.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

type ARAgingOverviewCardProps = {
  titleHref: string
  arAging: UseARAgingResult
}

export function ARAgingOverviewCard({
  titleHref,
  arAging,
}: ARAgingOverviewCardProps): React.ReactElement {
  const { cardMetrics, chartData, arValueAxis, loading, hasData, hasPayload } =
    arAging
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="AR aging"
      titleHref={titleHref}
      className="dashboard-ar-aging-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-ar-aging-content">
        <div className="dashboard-ar-aging-metrics">
          {cardMetrics.map((m) => (
            <DashboardMetricItem
              key={m.id ?? m.leftLabel}
              {...metricDataToItemPropsWithLayoutClass(
                m,
                'dashboard-ar-aging-metric',
              )}
            />
          ))}
        </div>
        <div className="dashboard-ar-aging-chart-area">
          <DashboardStackedBarChart
            data={chartData}
            series={[
              {
                dataKey: 'insurance',
                name: 'Insurance AR',
                color: 'var(--blue-400)',
              },
              {
                dataKey: 'patient',
                name: 'Patient AR',
                color: 'var(--orange-300)',
              },
            ]}
            horizontal={false}
            stacked={false}
            showLegend={false}
            barGap={5}
            barSize={50}
            yAxisDomain={arValueAxis.yDomain}
            yAxisTicks={arValueAxis.yTicks}
            yAxisTickMargin={6}
            valueFormatter={formatChartCurrencyDollars}
            height={280}
            margin={{ left: 8, right: 8, top: 8, bottom: 12 }}
            xAxisTickMargin={8}
            className="dashboard-ar-aging-chart"
          />
          <div className="dashboard-ar-aging-legend">
            <span className="dashboard-ar-aging-legend-item">
              <span
                className="dashboard-ar-aging-legend-dot"
                style={{ background: 'var(--blue-400)' }}
              />
              Insurance AR
            </span>
            <span className="dashboard-ar-aging-legend-item">
              <span
                className="dashboard-ar-aging-legend-dot"
                style={{ background: 'var(--orange-300)' }}
              />
              Patient AR
            </span>
          </div>
        </div>
      </div>
    </DashboardSectionCard>
  )
}
